import {
  DatabaseT,
  WorkGroupT,
  WorkspaceT,
  isStatusActive,
  isStatusDegraded,
  isStatusError,
  isStatusIdle,
  isStatusPaused,
  isStatusPending,
} from '@/pages/workgroup/type';

import { AccessFilter, FilterState, SortKey, StatusFilter, WorkspaceRow } from './types';

export function buildWorkspaceRows(group: WorkGroupT): WorkspaceRow[] {
  return group.workspaces.map((workspace) => ({
    workspace,
    databaseName:
      group.tg_databases.find((db) => db.database_id === workspace.database_id)?.name || workspace.database_name || '',
  }));
}

export function getUniqueWorkspaceSizes(workspaces: WorkspaceT[]): string[] {
  const sizes = new Set<string>();
  for (const workspace of workspaces) {
    if (workspace.workspace_type?.typeName) {
      sizes.add(workspace.workspace_type.typeName);
    }
  }
  return Array.from(sizes).sort();
}

function matchStatus(workspace: WorkspaceT, statusFilter: StatusFilter): boolean {
  if (statusFilter === 'all') {
    return true;
  }

  switch (statusFilter) {
    case 'active':
      return isStatusActive(workspace.status) && !isStatusDegraded(workspace.status);
    case 'stopped':
      return isStatusPaused(workspace.status);
    case 'idle':
      return isStatusIdle(workspace.status);
    case 'pending':
      return isStatusPending(workspace.status);
    case 'error':
      return isStatusError(workspace.status);
    case 'degraded':
      return isStatusDegraded(workspace.status);
    case 'terminating':
      return workspace.status === 'Terminating';
    default:
      return true;
  }
}

function matchAccess(workspace: WorkspaceT, accessFilter: AccessFilter): boolean {
  if (accessFilter === 'all') {
    return true;
  }
  return accessFilter === 'rw' ? workspace.is_rw : !workspace.is_rw;
}

function matchSearch(row: WorkspaceRow, searchInput: string): boolean {
  const search = searchInput.trim().toLowerCase();
  if (!search) {
    return true;
  }
  return (
    row.workspace.name.toLowerCase().includes(search) ||
    (row.databaseName ? row.databaseName.toLowerCase().includes(search) : false)
  );
}

function compareRows(a: WorkspaceRow, b: WorkspaceRow, sort: SortKey): number {
  switch (sort) {
    case 'name_asc':
      return a.workspace.name.localeCompare(b.workspace.name);
    case 'name_desc':
      return b.workspace.name.localeCompare(a.workspace.name);
    case 'size_asc':
      return (a.workspace.workspace_type?.typeName || '').localeCompare(b.workspace.workspace_type?.typeName || '');
    case 'size_desc':
      return (b.workspace.workspace_type?.typeName || '').localeCompare(a.workspace.workspace_type?.typeName || '');
    default:
      return a.workspace.name.localeCompare(b.workspace.name);
  }
}

export function applyWorkspaceFilters(rows: WorkspaceRow[], filters: FilterState): WorkspaceRow[] {
  const filtered = rows.filter(
    (row) =>
      matchSearch(row, filters.search) &&
      matchStatus(row.workspace, filters.status) &&
      matchAccess(row.workspace, filters.access) &&
      (filters.size === 'all' || row.workspace.workspace_type?.typeName === filters.size)
  );

  return filtered.sort((a, b) => compareRows(a, b, filters.sort));
}

export function parseMemoryToBytes(memory?: string): number | undefined {
  if (!memory) {
    return undefined;
  }
  // Handles "16Gi", "8G", "512Mi", "1Ti", "2TB", etc.
  const match = memory.trim().match(/^([\d.]+)\s*([KMGTP]i?B?)$/i);
  if (!match) {
    return undefined;
  }
  const value = parseFloat(match[1]);
  if (Number.isNaN(value)) {
    return undefined;
  }
  const unit = match[2].toUpperCase();
  // IEC binary (Ki, Mi, Gi, Ti) — used by Kubernetes resource strings
  const iecMultipliers: Record<string, number> = {
    KI: 1024,
    KIB: 1024,
    MI: 1024 ** 2,
    MIB: 1024 ** 2,
    GI: 1024 ** 3,
    GIB: 1024 ** 3,
    TI: 1024 ** 4,
    TIB: 1024 ** 4,
    PI: 1024 ** 5,
    PIB: 1024 ** 5,
  };
  // SI decimal
  const siMultipliers: Record<string, number> = {
    B: 1,
    KB: 1000,
    K: 1000,
    MB: 1000 ** 2,
    M: 1000 ** 2,
    GB: 1000 ** 3,
    G: 1000 ** 3,
    TB: 1000 ** 4,
    T: 1000 ** 4,
    PB: 1000 ** 5,
    P: 1000 ** 5,
  };
  const multiplier = iecMultipliers[unit] ?? siMultipliers[unit];
  if (multiplier === undefined) {
    return undefined;
  }
  return value * multiplier;
}

/**
 * Returns the bytes stored for a specific workspace from the database's folder_statistics.
 */
export function getWorkspaceFolderSize(database: DatabaseT | undefined, workspaceId: string): number | undefined {
  const entry = database?.folder_statistics?.workspaces?.find((w) => w.workspace_id === workspaceId);
  return entry?.size;
}

export function calculateUtilizationPercent(usedBytes: number, totalBytes: number): number {
  if (!totalBytes) {
    return 0;
  }
  return Math.min(100, Math.round((usedBytes / totalBytes) * 100));
}
